/**
 * Main App Component
 * Portal Frontend - Admin, Moderator, Student, and Guardian portal
 *
 * Routing rules (via ?mod= URL param):
 *  - no param (default) → linked family devices render the profile picker
 *    directly; unlinked browsers get the LandingPage (THE unified email-link
 *    sign-in for guardians AND students)
 *  - ?mod=admin      → Admin password login → Admin dashboard  (PONDER_ADMIN)
 *  - ?mod=moderator  → Moderator password login → Moderator dashboard
 *  - ?mod=test-student → Username+password login for admin-created (test)
 *    students, styled like the portal root
 *  - ?login=<token>  → Co-guardian invite link landing
 *  - legacy ?mode=moderator → treated as mod=moderator
 *  - (?mod=student / ?mod=guardian / ?mod=family / ?signup=guardian were
 *    removed — sign-in AND signup live inside the landing; the picker is
 *    recognized automatically from the device link state)
 *  - local dev (no overrides) → LocalDevPicker: choose a user to log in as
 */

import { useEffect, useRef, useMemo } from 'react'
import { PortalLoginScreen } from './features/login/portal-login-screen'
import { ModeratorPortal } from './features/moderator/moderator-dashboard'
import { AdminDashboard } from './features/admin/admin-dashboard'
import { StudentDashboard } from './features/student/student-dashboard'
import { LandingPage } from './features/login/landing-page'
import { FamilyProfilePicker } from './features/login/family-profile-picker'
import { TestStudentLoginScreen } from './features/login/test-student-login-screen'
import { GuardianLinkLanding } from './features/login/guardian-link-landing'
import { GuardianDashboard } from './features/guardian/guardian-dashboard'
import { LocalDevPicker } from './features/local-dev/local-dev-picker'
import { CertificateValidationPage } from './features/student/certificate/certificate-validation-page'
import { UiGallery } from './components/ui-gallery'
import { RoleGuard } from './guards/role-guard'
import { useAuthStore } from './services/authentication/auth-store'
import { hasFamilyDeviceToken } from './services/family-device-storage'
import { version } from './version'
import type { StaffRole } from './api/portal-user'

function App() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);

  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const isLocal = version.environment === 'development';

  // Resolve the portal mode from URL params (?mod= takes precedence, legacy ?mode= still works).
  // Only staff password logins keep a dedicated mode — guardians and students
  // sign in through the unified landing (email link) or the family picker.
  const modParam = urlParams.get('mod') ?? urlParams.get('mode');
  const portalMode: StaffRole | null = (() => {
    if (modParam === 'admin') return 'admin';
    if (modParam === 'moderator') return 'moderator';
    return null;
  })();

  // Username+password login for admin-created (test) students
  const showTestStudentLogin = modParam === 'test-student';
  // Passwordless magic-login link landing (?login=<token> — also how
  // co-guardian invites are accepted)
  const loginToken = urlParams.get('login');

  const forceGuestPage = urlParams.get('showGuestLandingPage') === 'true';

  // Public certificate validation (`?validate`, optionally `?validate=<code>`) —
  // no login required; checked first so a shared validation link works for anyone.
  const showValidate = urlParams.has('validate');

  // `?ui-gallery` — every UI primitive on one page, for visual regression.
  // Dev-only: it is not part of the product and should never be reachable in a
  // deployed build. `import.meta.env.DEV` lets the bundler drop it entirely.
  const showUiGallery = import.meta.env.DEV && urlParams.has('ui-gallery');

  const prevAuthenticated = useRef(false);
  useEffect(() => {
    prevAuthenticated.current = isAuthenticated;
  }, [isAuthenticated]);

  // Local dev with no mod param → show the dev user picker (before and after logout)
  const showLocalDevPicker = isLocal && !portalMode && !forceGuestPage;

  if (showUiGallery) {
    return <UiGallery />;
  }

  if (showValidate) {
    return <CertificateValidationPage initialCode={urlParams.get('validate') ?? ''} />;
  }

  // Emailed login/invite links must always open, even with a session active
  // on this browser (completing them replaces the session).
  if (loginToken) {
    return <GuardianLinkLanding token={loginToken} />;
  }

  // Authenticated: show the dashboard matching the user's role
  if (isAuthenticated && user) {
    switch (user.role) {
      case 'admin':
        return (
          <RoleGuard allowedRoles={['admin']}>
            <AdminDashboard />
          </RoleGuard>
        );
      case 'moderator':
        return (
          <RoleGuard allowedRoles={['moderator', 'admin']}>
            <ModeratorPortal />
          </RoleGuard>
        );
      case 'student':
        return (
          <RoleGuard allowedRoles={['student']}>
            <StudentDashboard />
          </RoleGuard>
        );
      case 'guardian':
        return (
          <RoleGuard allowedRoles={['guardian']}>
            <GuardianDashboard />
          </RoleGuard>
        );
    }
  }

  // Username+password login for admin-created (test) students
  if (showTestStudentLogin) {
    return <TestStudentLoginScreen />;
  }

  // Local dev with no mod param → show the picker (unauthenticated state)
  if (showLocalDevPicker) {
    return <LocalDevPicker />;
  }

  // Non-local envs and no mode: linked family devices skip the landing and go
  // straight to the profile picker; the landing (email login / create account)
  // only shows on unlinked browsers — e.g. after "sign out of this device".
  // The forceGuestPage override still previews the landing in local dev.
  if (!portalMode && (!isLocal || forceGuestPage)) {
    if (hasFamilyDeviceToken() && !forceGuestPage) {
      return <FamilyProfilePicker />;
    }
    return <LandingPage />;
  }

  // Staff password login (?mod=admin / ?mod=moderator)
  return <PortalLoginScreen mode={portalMode ?? 'admin'} />;
}

export default App
